import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { OrganizationMember } from '../../../types/domain';
import { InnerLayoutSection } from '../../../components/layout/InnerLayout';

interface AdminOrgMembersTabProps {
  members: OrganizationMember[];
  onRemoveMember: (member: OrganizationMember) => void;
}

export const AdminOrgMembersTab: React.FC<AdminOrgMembersTabProps> = ({
  members,
  onRemoveMember,
}) => {
  return (
    <InnerLayoutSection
      title={`Organization Members (${members.length})`}
      description="Users with membership access to this organization."
    >

      {members.length === 0 ? (
        <div className="bg-cf-surface border border-cf-border rounded-lg p-8 text-center text-cf-textMuted">
          No members found in this organization.
        </div>
      ) : (
        <div className="bg-cf-surface border border-cf-border rounded-lg overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-cf-border bg-cf-tableHeader text-xs font-semibold text-cf-textSecondary">
                <th className="py-2.5 px-4">User</th>
                <th className="py-2.5 px-4">Role</th>
                <th className="py-2.5 px-4">User ID</th>
                <th className="py-2.5 px-4">Joined</th>
                <th className="py-2.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cf-border">
              {members.map((member) => {
                const memberId = member.user_id || member.id;
                const memberName = member.user?.name || member.user?.email || member.name || member.email || 'Member';
                const memberEmail = member.user?.email || member.email || '';
                return (
                  <tr key={member.id} className="hover:bg-cf-surfaceHover transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-semibold text-cf-textPrimary">{memberName}</div>
                      {memberEmail && memberEmail !== memberName && (
                        <div className="text-xs text-cf-textMuted">{memberEmail}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-1.5 py-0.5 rounded bg-cf-bg border border-cf-border text-xs uppercase font-medium">
                        {member.role || 'member'}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-cf-textSecondary text-xs">
                      {memberId}
                    </td>
                    <td className="py-3 px-4 text-cf-textMuted">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/tenants/${memberId}`}
                          className="px-2 py-1 rounded bg-cf-bg hover:bg-cf-surfaceHover border border-cf-border text-cf-textSecondary hover:text-cf-textPrimary transition-colors text-xs flex items-center gap-1"
                          title="Inspect User Account"
                        >
                          <span>Inspect</span>
                          <ExternalLink size={10} />
                        </Link>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => onRemoveMember(member)}
                          className="text-cf-textMuted hover:text-rose-400"
                          title="Remove from Organization"
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </InnerLayoutSection>
  );
};
